import { useEffect, useMemo, useRef } from "react";
import { getOpener } from "../utils/focus.ts";
import A11yDialog from "a11y-dialog";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";

export default function useDialog(onRequestClose: () => void) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | undefined>();
  const isNestedRef = useRef(false);

  // need to get focus early in case dialog content does a set focus
  // on mount and useDialog gets that focus instead!
  // allydialog is supposed to set focus, but we find it patchy
  // and, sometimes we use fallbacks for disappearing openers
  if (!openerRef.current) {
    openerRef.current = getOpener();
  }

  useEffect(() => {
    const dialog = new A11yDialog(dialogRef.current!);
    dialog.show();
    dialog.on("hide", () => {
      onRequestClose();
    });
  }, [onRequestClose]);

  useEffect(() => {
    // restore focus to opener on unmount
    return () => {
      openerRef.current?.focus();
    };
  }, []);

  useEffect(() => {
    const el = scrollRef.current ?? dialogRef.current!;
    disableBodyScroll(el, { reserveScrollBarGap: true });
    return () => {
      if (!isNestedRef.current) {
        enableBodyScroll(el);
      }
    };
  }, []);

  /**
   * a11y-dialog technically supports nested dialogs if rendered inside one
   * another though they don't recommend using it. The behavior of multiple
   * aria-modal elements is not defined and layered dialogs don't look great.
   *
   * In some places we have multi-dialogs managed with an explicit switcher,
   * but in other cases that can be cumbersome. This effect will fully hide any
   * already-open dialog when the secondary dialog opens and then re-show it
   * when it closes.
   *
   * Depth of more than 2 not currently supported.
   */
  useEffect(() => {
    const otherDialog = Array.from(
      document.querySelectorAll<HTMLDivElement>(
        "[aria-modal='true'][role='dialog']"
      )
    ).find((el) => el.id !== dialogRef.current!.id);
    if (otherDialog) {
      isNestedRef.current = true;
      otherDialog.style.display = "none";
    }

    return () => {
      if (otherDialog) {
        otherDialog.style.display = "";
        otherDialog.focus();
      }
    };
  }, []);

  return useMemo(
    () => ({
      dialogRef,
      scrollRef,
    }),
    []
  );
}
